import React from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import styled from 'styled-components';

const DEFAULT_TOOLBAR = [
  [{ header: [1, 2, 3, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['link'],
  ['clean'],
];

/**
 * Full toolbar: adds font family/size, text and highlight colour, and
 * alignment. Opt in per usage - the Wiki popups deliberately keep the compact
 * default. Everything here is covered by the allowlist in utils/sanitizeHtml.
 */
export const RICH_TOOLBAR = [
  [{ font: [] }, { size: ['small', false, 'large', 'huge'] }],
  [{ header: [1, 2, 3, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ color: [] }, { background: [] }],
  [{ align: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['blockquote', 'link'],
  ['clean'],
];

interface Props {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: any;
  isRequired?: boolean;
  disabled?: boolean;
  /** Override the toolbar rows, e.g. RICH_TOOLBAR for colour and font. */
  toolbar?: any[];
  placeholder?: string;
  /** Taller editing area for long-form content. */
  tall?: boolean;
}

const RichTextEditorWithLabel: React.FC<Props> = ({
  label,
  value,
  onChange,
  error,
  isRequired,
  disabled = false,
  toolbar,
  placeholder,
  tall = false,
}) => {
  return (
    <Wrapper>
      <Label>
        {label}
        {isRequired && <Required>*</Required>}
      </Label>
      <EditorWrap $error={!!error} $tall={tall}>
        <ReactQuill
          value={value || ''}
          onChange={onChange}
          readOnly={disabled}
          placeholder={placeholder}
          modules={{
            toolbar: toolbar || DEFAULT_TOOLBAR,
          }}
        />
      </EditorWrap>
      {error && <ErrorText>{error.message}</ErrorText>}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Label = styled.label`
  font-size: var(--fs-base);
  font-weight: var(--fw-regular);
  color: var(--neutral-800);
  margin-bottom: var(--space-2);
  line-height: 21px;
`;

const Required = styled.span`
  color: var(--danger-500);
  margin-left: 2px;
`;

const EditorWrap = styled.div<{ $error: boolean; $tall?: boolean }>`
  .ql-toolbar {
    border: 1px solid ${({ $error }) => $error ? 'var(--danger-500)' : 'var(--neutral-300)'};
    border-radius: var(--radius-md) var(--radius-md) 0 0;
    background: var(--neutral-50);
    padding: var(--space-2) var(--space-3);
    display: flex;
    flex-wrap: wrap;
    gap: 2px;

    .ql-formats {
      display: inline-flex;
      align-items: center;
      gap: 2px;
      margin-right: var(--space-2);
    }

    button {
      width: 32px;
      height: 32px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: var(--radius-sm);
      border: 0;
      background: transparent;
      color: var(--neutral-600);
      cursor: pointer;
      transition: background-color var(--motion-fast) var(--ease-standard),
        color var(--motion-fast) var(--ease-standard);

      &:hover {
        background: var(--neutral-200);
        color: var(--neutral-900);
      }

      &.ql-active {
        background: var(--primary-50);
        color: var(--primary-700);
      }

      svg {
        width: 18px;
        height: 18px;
      }

      .ql-stroke { stroke: currentColor; }
      .ql-fill { fill: currentColor; }
    }

    .ql-picker {
      height: 32px;

      .ql-picker-label {
        display: flex;
        align-items: center;
        padding: 0 var(--space-2);
        border: 1px solid var(--neutral-300);
        border-radius: var(--radius-sm);
        font-size: var(--fs-sm);
        height: 32px;
        color: var(--neutral-700);

        &:hover {
          color: var(--primary-600);
          border-color: var(--primary-400);
        }

        svg {
          width: 14px;
          height: 14px;
        }
      }

      .ql-picker-options {
        border: 1px solid var(--neutral-200);
        border-radius: var(--radius-md);
        box-shadow: var(--shadow-md);
        background: var(--neutral-0);
        padding: var(--space-1);

        .ql-picker-item {
          padding: var(--space-1) var(--space-2);
          border-radius: var(--radius-sm);

          &:hover {
            background: var(--primary-50);
            color: var(--primary-700);
          }
        }
      }
    }
  }

  .ql-container {
    min-height: ${({ $tall }) => ($tall ? '200px' : '120px')};
    border: 1px solid ${({ $error }) => $error ? 'var(--danger-500)' : 'var(--neutral-300)'};
    border-top: 0;
    border-radius: 0 0 var(--radius-md) var(--radius-md);
    font-size: var(--fs-base);
    font-family: inherit;
  }

  .ql-editor {
    color: var(--neutral-800);
    min-height: ${({ $tall }) => ($tall ? '180px' : '100px')};
  }

  /* The colour/background pickers render swatch grids, not text rows. */
  .ql-color-picker .ql-picker-options,
  .ql-background .ql-picker-options {
    width: 168px;
    padding: var(--space-2);
  }

  .ql-color-picker .ql-picker-item,
  .ql-background .ql-picker-item {
    width: 18px;
    height: 18px;
    margin: 2px;
    border: 1px solid var(--neutral-200);
    border-radius: var(--radius-sm);
  }

  .ql-picker.ql-font,
  .ql-picker.ql-size {
    width: auto;
    min-width: 92px;
  }

  .ql-editor.ql-blank::before {
    color: var(--neutral-400);
    font-style: normal;
  }
`;

const ErrorText = styled.div`
  color: var(--danger-500);
  font-size: var(--fs-sm);
  margin-top: var(--space-1);
`;

export default RichTextEditorWithLabel;
